import { useEffect, useState } from "react";
import {
    Sparkles, Database, Cloud, Bot, Cpu, GitBranch, Activity, FileText, Check,
} from "lucide-react";

/**
 * Premium "AI Operating Layer" hero visual.
 * - Layered glass panels giving real-product feel
 * - Live activity feed (rotates every ~2s)
 * - Streaming metric ticker
 * - Floating capability chips
 * - Subtle orbit rings
 */
const ACTIVITY = [
    { ico: <FileText className="w-3.5 h-3.5" />, text: "DocuIQX classified 412 contracts", t: "now" },
    { ico: <Sparkles className="w-3.5 h-3.5" />, text: "AgentIQX resolved 28 HR tickets", t: "2s" },
    { ico: <Bot className="w-3.5 h-3.5" />, text: "TalentIQX ranked 1,204 profiles", t: "6s" },
    { ico: <Activity className="w-3.5 h-3.5" />, text: "RecruitIQX synced 6 vendors", t: "12s" },
];

export default function HeroAIVisual() {
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => (t + 1) % ACTIVITY.length), 2200);
        return () => clearInterval(id);
    }, []);

    const visible = ACTIVITY.slice(tick).concat(ACTIVITY.slice(0, tick));

    return (
        <div
            className="relative w-full aspect-[4/3.6] md:aspect-[4/3.2]"
            data-testid="hero-ai-visual"
            aria-hidden="true"
        >
            {/* Soft radial glow */}
            <div
                className="absolute inset-0 rounded-[32px]"
                style={{
                    background:
                        "radial-gradient(58% 50% at 50% 38%, rgba(255,64,64,0.20) 0%, rgba(255,64,64,0) 65%)",
                }}
            />

            {/* Center dashboard card — premium glass, 3D-tilted scene */}
            <div className="absolute inset-0 grid place-items-center pqx-scene">
                <div className="relative w-[80%] max-w-md pqx-anim-float-slow pqx-tilt-3d">
                    {/* Soft outer drop shadow */}
                    <div
                        className="absolute -inset-2 rounded-[28px] blur-2xl opacity-50"
                        style={{ background: "radial-gradient(closest-side, rgba(0,0,0,0.55), transparent 70%)" }}
                    />
                    <div className="relative rounded-2xl bg-white border border-[#303030]/10 shadow-[0_30px_70px_-30px_rgba(0,0,0,0.6)] overflow-hidden">
                        {/* Window chrome */}
                        <div className="flex items-center justify-between px-5 py-3 border-b border-[#303030]/8 bg-[#FAFAF8]">
                            <div className="flex items-center gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-full bg-[#FF4040]" />
                                <span className="w-2.5 h-2.5 rounded-full bg-[#303030]/15" />
                                <span className="w-2.5 h-2.5 rounded-full bg-[#303030]/15" />
                            </div>
                            <span className="font-mono text-[10px] text-[#303030]/60 uppercase tracking-[0.18em]">
                                perceptiqx · operating layer
                            </span>
                            <span className="inline-flex items-center gap-1 text-[10px] text-[#FF4040] font-semibold font-mono">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#FF4040] pqx-anim-blink" />
                                live
                            </span>
                        </div>

                        <div className="p-5 space-y-4">
                            {/* Title row */}
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-[9.5px] uppercase tracking-[0.2em] text-[#303030]/55">
                                        AI Operating Layer
                                    </p>
                                    <p className="mt-1 text-[15px] font-semibold text-[#303030]">
                                        Talent · Documents · Workflows
                                    </p>
                                </div>
                                <span className="font-mono text-[10px] text-[#303030]/55 mt-1">
                                    v2.4.1
                                </span>
                            </div>

                            {/* Metric trio */}
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    { l: "Agents", v: "12" },
                                    { l: "Tasks", v: "2.4k" },
                                    { l: "Accuracy", v: "97.4%" },
                                ].map((s) => (
                                    <div
                                        key={s.l}
                                        className="rounded-lg border border-[#303030]/8 px-2.5 py-2 bg-white"
                                    >
                                        <p className="text-[9.5px] uppercase tracking-[0.15em] text-[#303030]/55">
                                            {s.l}
                                        </p>
                                        <p className="mt-1 text-base font-semibold text-[#303030] font-mono">
                                            {s.v}
                                        </p>
                                    </div>
                                ))}
                            </div>

                            {/* Mini bar chart */}
                            <div className="rounded-lg border border-[#303030]/8 p-3 bg-white">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-[9.5px] uppercase tracking-[0.18em] text-[#303030]/55">
                                        Throughput / hr
                                    </p>
                                    <p className="text-[10px] text-[#FF4040] font-mono font-semibold">
                                        ↑ 12.8%
                                    </p>
                                </div>
                                <div className="flex items-end gap-1 h-14">
                                    {[40, 60, 48, 72, 55, 88, 70, 92, 65, 80, 58, 95].map((h, i) => (
                                        <div
                                            key={i}
                                            className="flex-1 rounded-t-sm transition-all"
                                            style={{
                                                height: `${h}%`,
                                                background:
                                                    i === 11
                                                        ? "#FF4040"
                                                        : i % 3 === 0
                                                        ? "#303030"
                                                        : "rgba(48,48,48,0.18)",
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Activity feed */}
                            <div className="rounded-lg border border-[#303030]/8 bg-white overflow-hidden">
                                <div className="px-3 py-2 border-b border-[#303030]/8 flex items-center justify-between">
                                    <p className="text-[9.5px] uppercase tracking-[0.18em] text-[#303030]/55">
                                        Activity
                                    </p>
                                    <span className="inline-flex items-center gap-1 text-[10px] text-[#303030]/55 font-mono">
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#FF4040] pqx-anim-blink" />
                                        streaming
                                    </span>
                                </div>
                                <ul className="divide-y divide-[#303030]/6">
                                    {visible.slice(0, 3).map((a, i) => (
                                        <li
                                            key={`${tick}-${i}`}
                                            className="flex items-center gap-2.5 px-3 py-2 pqx-reveal"
                                            style={{ animationDelay: `${i * 60}ms` }}
                                        >
                                            <span className="w-6 h-6 grid place-items-center rounded-md bg-[#FF4040]/8 text-[#FF4040] shrink-0">
                                                {a.ico}
                                            </span>
                                            <p className="text-[11.5px] text-[#303030] flex-1 truncate">
                                                {a.text}
                                            </p>
                                            <span className="font-mono text-[10px] text-[#303030]/50">
                                                {a.t}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Floating mini candidate-ranking panel — adds product depth */}
            <div className="hidden md:block absolute -bottom-4 left-[-6%] w-[210px] pqx-anim-float" style={{ animationDelay: "1.2s" }}>
                <div className="rounded-xl bg-white border border-[#303030]/10 shadow-[0_24px_50px_-26px_rgba(0,0,0,0.6)] overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-[#303030]/8 bg-[#FAFAF8]">
                        <span className="text-[10px] font-semibold text-[#303030]">TalentIQX · top match</span>
                        <span className="font-mono text-[9px] text-[#303030]/50">#1</span>
                    </div>
                    <div className="px-3 py-2.5 flex items-center gap-2.5">
                        <span className="w-7 h-7 grid place-items-center rounded-lg bg-[#303030] text-white text-[10px] font-semibold">A.K</span>
                        <div className="flex-1">
                            <div className="h-1.5 rounded-full bg-[#303030]/8 overflow-hidden">
                                <div className="h-full w-[96%] rounded-full bg-[#FF4040] pqx-scorebar" />
                            </div>
                            <p className="mt-1 text-[9.5px] text-[#303030]/55">Senior ML Engineer</p>
                        </div>
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#FF4040]">
                            <Check className="w-3 h-3" />96
                        </span>
                    </div>
                </div>
            </div>

            {/* Floating glass chips */}
            <FloatingChip
                icon={<Bot className="w-4 h-4" />}
                label="AI Agents"
                sub="24 active"
                className="top-0 left-0 pqx-anim-float"
                accent
            />
            <FloatingChip
                icon={<Sparkles className="w-4 h-4" />}
                label="Talent Intel"
                sub="3,210 profiles"
                className="top-8 right-0 pqx-anim-float-slow"
            />
            <FloatingChip
                icon={<Database className="w-4 h-4" />}
                label="Data"
                sub="38 sources"
                className="bottom-16 -left-1 md:left-2 pqx-anim-float-slow"
            />
            <FloatingChip
                icon={<Cpu className="w-4 h-4" />}
                label="Product Eng."
                sub="6 squads"
                className="bottom-2 right-2 md:right-6 pqx-anim-float"
                accent
            />
            <FloatingChip
                icon={<Cloud className="w-4 h-4" />}
                label="Cloud"
                sub="multi-region"
                className="top-1/2 -translate-y-1/2 -left-2 md:-left-4 pqx-anim-drift hidden md:flex"
            />
            <FloatingChip
                icon={<GitBranch className="w-4 h-4" />}
                label="Workflow"
                sub="412/d · auto"
                className="top-1/2 -translate-y-1/2 -right-2 md:-right-4 pqx-anim-drift hidden md:flex"
            />

            {/* Orbiting accent rings */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-[6%] rounded-full border border-dashed border-[#303030]/12 pqx-anim-rotate-slow" />
                <div
                    className="absolute inset-[14%] rounded-full border border-[#FF4040]/15 pqx-anim-rotate-slow"
                    style={{ animationDuration: "90s", animationDirection: "reverse" }}
                />
            </div>
        </div>
    );
}

function FloatingChip({ icon, label, sub, className = "", accent = false }) {
    return (
        <div
            className={`absolute inline-flex items-center gap-3 rounded-xl pqx-glass px-3 py-2.5 shadow-[0_14px_32px_-20px_rgba(48,48,48,0.55)] ${className}`}
        >
            <span
                className={`w-8 h-8 grid place-items-center rounded-lg ${
                    accent ? "bg-[#FF4040] text-white" : "bg-[#303030] text-[#FAFAF8]"
                }`}
            >
                {icon}
            </span>
            <div className="leading-tight">
                <p className="text-[11.5px] font-semibold text-[#303030]">{label}</p>
                <p className="text-[10px] text-[#303030]/55 font-mono">{sub}</p>
            </div>
        </div>
    );
}
